/**
 * @file AbaStaffingForSmallAgencies.jsx
 * @description Problem-intent SEO landing page for owners of small ABA agencies
 *   comparing job boards, social groups, recruiters, and ABAcases.
 *   Section order: hero, will providers see my case (+ honest newer-areas note),
 *   what one case costs, who is behind ABAcases, how the options compare,
 *   not a generic job board, FAQ + local guides, final CTA.
 *   All ABAcases prices, counts and dates come from Admin config via
 *   getAgencyPricing(); nothing is hardcoded here.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfigVersion } from "../lib/useConfigVersion";
import { T } from "../theme";
import { ChevronRightIcon } from "./Icons";
import PublicFooter from "./PublicFooter";
import StickyMobileCta from "./StickyMobileCta";
import ContactModal from "./ContactModal";
import { FaqDetails } from "./Disclosure";
import { getStarterPromoDates } from "../lib/plans";
import { getAgencyPricing } from "../lib/agencyPricing";
import { useBreadcrumbJsonLd, useFaqJsonLd, useSiteSchema } from "../lib/seoSchema";
import { seoHref, usePageSeo } from "../lib/seo";
import { useAgencySignup } from "./AgencyFinalCta";
import { ProofChips, Scorecard, DifferentialGrid, FounderCard, OutsideFloridaNote, outsideFloridaText, agencyChips } from "./AgencyVisuals";
import CasePromotion from "./CasePromotion";

const NAVY = "#0F1024";
const NAVY_MID = "#1E1B4B";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818CF8";
const TEXT = "#475569";
const BORDER = "rgba(226,232,240,0.9)";
const ALT_BG = "#F8FAFC";

// ── Copy ─────────────────────────────────────────────────────────────────────
// One builder per language. Anything that depends on Admin config (price, promo
// count, dates) is a function of `p` (getAgencyPricing), so EN and ES never need
// index-based special cases. The canonical/prerendered SEO surface (title, meta,
// FAQ JSON-LD) stays English; Spanish is UI-only and follows the language switcher.

const count = (n, one, many) => (n === 1 ? one : many);

const EN = (p) => {
  return {
    home: "Home",
    forAgencies: "For Agencies",
    crumb: "Small-agency staffing",
    eyebrow: "FOR INDEPENDENT ABA PRACTICES",
    h1: "ABA staffing for small agencies, without recruiter fees or ad spend",
    sub: "Skip the click fees and recruiter fees. Post the case, review matched ABA providers, hire.",
    heroPrice: p.isPromoActive
      ? `No credit card · ${count(p.count, "first opening", `first ${p.count} openings`)} free through ${p.endDateShort} · then Starter from $${p.starterMo}/mo`
      : `No credit card to sign up · Starter $${p.starterMo}/mo with ${p.starterIncluded} ${count(p.starterIncluded, "opening", "openings")} included`,
    costTitle: "What one case costs",
    costSub: "One open case, three ways to pay for it.",
    cards: {
      click: { name: "Pay-per-click boards", sub: "Indeed, ZipRecruiter, and similar", big: "Per click", body: "You pay for every click, whether or not the person is qualified." },
      recruiter: { name: "Recruiters", sub: "Staffing agencies", big: "Per hire", body: "A placement fee for every hire." },
      abacases: {
        name: "ABAcases",
        sub: "Built for ABA",
        big: p.isPromoActive ? "$0" : `$${p.starterMo}`,
        lead: p.isPromoActive ? `for your ${count(p.count, "first opening", `first ${p.count} openings`)}, no credit card` : `a month on Starter, ${p.starterIncluded} ${count(p.starterIncluded, "opening", "openings")} included`,
        body: p.isPromoActive
          ? `From ${p.startDate}: Starter is $${p.starterMo} a month with ${p.starterIncluded} ${count(p.starterIncluded, "opening", "openings")} included, only if you choose a plan.`
          : `Extra openings are $${p.starterExtra} each. Fill or close an opening and it stops counting toward your plan.`,
      },
    },
    proLine: `Hiring a lot? Pro is $${p.proMo} a month with ${p.proCases} openings and ${p.teamLogins} team logins included. Extra openings are $${p.proExtra} each on Pro.${p.workforceIncluded ? ` ABAcases Workforce is included: up to ${p.workforceMaxStarter} people on Starter and up to ${p.workforceMaxPro} on Pro.` : ""}`,
    founderLine: "Questions? Contact support.",
    compareTitle: "How the options compare",
    compareWith: "Compare with:",
    channels: [
      { title: "Pay-per-click boards", short: "Job boards", sub: "Indeed and similar" },
      { title: "Social groups", short: "Social", sub: "Facebook, WhatsApp" },
      { title: "Recruiters", short: "Recruiters", sub: "Placement fee" },
      { title: "ABAcases", short: "ABAcases", sub: "Built for ABA", highlight: true },
    ],
    rows: [
      { label: "Cost model", values: ["Per click", "Free", "Per hire", "Flat per case"] },
      { label: "Biggest audience today", values: ["yes", "partial", "partial", "partial"] },
      { label: "Only ABA providers (BT, RBT, BCaBA, BCBA)", values: ["no", "no", "partial", "yes"] },
      { label: "Provider credentials verified (BACB)", values: ["no", "no", "partial", "yes"] },
      { label: "Every agency verified (federal NPI registry)", values: ["no", "no", "no", "yes"] },
      { label: "One shared hiring pipeline", values: ["partial", "no", "no", "yes"] },
      { label: "Fit scored for your case", values: ["no", "no", "partial", "yes"] },
      { label: "You own the relationship", values: ["yes", "yes", "no", "yes"] },
      { label: "Predictable monthly cost", values: ["no", "yes", "no", "yes"] },
    ],
    faqTitle: "Questions small ABA agencies ask before trying ABAcases",
    faqs: [
      { q: "Will providers in my area see my case?", a: "Yes. Every matching provider near your case gets an alert, and you can see who is nearby on the provider map. Provider availability is seasonal, so a clear pay range and a workable schedule are what make your case stand out." },
      {
        q: "Is there a free way to post ABA cases?",
        a: p.isPromoActive
          ? `Yes. During the free period, your first ${p.count} ${count(p.count, "opening is", "openings are")} free through ${p.endDate}. From ${p.startDate}, Starter is $${p.starterMo} a month and includes ${p.starterIncluded} ${count(p.starterIncluded, "opening", "openings")}.`
          : `The free period has ended. Starter is $${p.starterMo} a month and includes ${p.starterIncluded} ${count(p.starterIncluded, "opening", "openings")}. Close or fill an opening and it stops counting toward your plan.`,
      },
      {
        q: "How much does it cost to hire an RBT?",
        a: p.isPromoActive
          ? `On ABAcases, Starter is $${p.starterMo} a month from ${p.startDate}, with ${p.starterIncluded} ${count(p.starterIncluded, "opening", "openings")} included. Pay-per-click boards charge for every visitor, and recruiters usually charge a placement fee for every hire.`
          : `On ABAcases, Starter is $${p.starterMo} a month with ${p.starterIncluded} ${count(p.starterIncluded, "opening", "openings")} included. Pay-per-click boards charge for every visitor, and recruiters usually charge a placement fee for every hire.`,
      },
      {
        q: "How is this different from posting in a Facebook group?",
        a: "Social groups can provide useful reach, but replies are scattered across comments and messages. ABAcases keeps each case, candidate, credential status, fit comparison, conversation, and hiring stage in one workflow.",
      },
      {
        q: "Do I need a credit card to start?",
        a: p.isPromoActive
          ? `No. During the free period you can open an agency account and post your openings without adding a credit card.`
          : `You can open an agency account without a credit card. Starter is $${p.starterMo} a month and includes ${p.starterIncluded} ${count(p.starterIncluded, "opening", "openings")}.`,
      },
      {
        q: "What if I only hire once or twice a year?",
        a: "Openings are reusable. You can post a case, fill or close it, and use the same room in your plan again when the next need appears instead of paying for a new post each time.",
      },
      {
        q: "Can I see providers before they apply?",
        a: "Yes. The provider map is open on every plan. It shows relevant provider information and distance around a selected case, and you can filter providers by language, including Spanish. You can also invite matched providers to apply.",
      },
    ],
    localQ: "Hiring in Florida?",
    localFl: "Florida guide",
    localMiami: "Miami page",
  };
};

const ES = (p) => {
  return {
    home: "Inicio",
    forAgencies: "Para agencias",
    crumb: "Contratación para agencias pequeñas",
    eyebrow: "PARA PRÁCTICAS ABA INDEPENDIENTES",
    h1: "Contratación ABA para agencias pequeñas, sin comisiones de reclutadores ni gasto en anuncios",
    sub: "Evita los cobros por clic y las comisiones de reclutadores. Publica el caso, revisa proveedores ABA compatibles y contrata.",
    heroPrice: p.isPromoActive
      ? `Sin tarjeta · ${count(p.count, "primera vacante gratis", `primeras ${p.count} vacantes gratis`)} hasta ${p.endDateShort} · luego Starter desde $${p.starterMo}/mes`
      : `Sin tarjeta para registrarte · Starter $${p.starterMo}/mes con ${p.starterIncluded} ${count(p.starterIncluded, "vacante incluida", "vacantes incluidas")}`,
    costTitle: "Cuánto cuesta un caso",
    costSub: "Un caso abierto, tres formas de pagarlo.",
    cards: {
      click: { name: "Bolsas de pago por clic", sub: "Indeed, ZipRecruiter y similares", big: "Por clic", body: "Pagas por cada clic, sea o no una persona calificada." },
      recruiter: { name: "Reclutadores", sub: "Agencias de personal", big: "Por contratación", body: "Una comisión de colocación por cada contratación." },
      abacases: {
        name: "ABAcases",
        sub: "Hecho para ABA",
        big: p.isPromoActive ? "$0" : `$${p.starterMo}`,
        lead: p.isPromoActive ? `por tu ${count(p.count, "primera vacante", `primeras ${p.count} vacantes`)}, sin tarjeta` : `al mes en Starter, con ${p.starterIncluded} ${count(p.starterIncluded, "vacante incluida", "vacantes incluidas")}`,
        body: p.isPromoActive
          ? `Desde el ${p.startDate}: Starter cuesta $${p.starterMo} al mes con ${p.starterIncluded} ${count(p.starterIncluded, "vacante incluida", "vacantes incluidas")}, solo si eliges un plan.`
          : `Las vacantes extra cuestan $${p.starterExtra} cada una. Cúbrela o ciérrala y deja de contar para tu plan.`,
      },
    },
    proLine: `¿Contratas mucho? Pro cuesta $${p.proMo} al mes con ${p.proCases} vacantes y ${p.teamLogins} accesos de equipo incluidos. Las vacantes extra cuestan $${p.proExtra} cada una en Pro.${p.workforceIncluded ? ` ABAcases Workforce está incluido: hasta ${p.workforceMaxStarter} personas en Starter y hasta ${p.workforceMaxPro} en Pro.` : ""}`,
    founderLine: "¿Preguntas? Contacta a soporte.",
    compareTitle: "Cómo se comparan las opciones",
    compareWith: "Comparar con:",
    channels: [
      { title: "Bolsas de pago por clic", short: "Bolsas", sub: "Indeed y similares" },
      { title: "Grupos sociales", short: "Redes", sub: "Facebook, WhatsApp" },
      { title: "Reclutadores", short: "Reclutadores", sub: "Comisión por colocación" },
      { title: "ABAcases", short: "ABAcases", sub: "Hecho para ABA", highlight: true },
    ],
    rows: [
      { label: "Modelo de costo", values: ["Por clic", "Gratis", "Por contratación", "Fijo por caso"] },
      { label: "Mayor audiencia hoy", values: ["yes", "partial", "partial", "partial"] },
      { label: "Solo proveedores ABA (BT, RBT, BCaBA, BCBA)", values: ["no", "no", "partial", "yes"] },
      { label: "Credenciales de proveedores verificadas (BACB)", values: ["no", "no", "partial", "yes"] },
      { label: "Cada agencia verificada (registro federal NPI)", values: ["no", "no", "no", "yes"] },
      { label: "Un solo proceso de contratación", values: ["partial", "no", "no", "yes"] },
      { label: "Compatibilidad calculada para tu caso", values: ["no", "no", "partial", "yes"] },
      { label: "La relación es tuya", values: ["yes", "yes", "no", "yes"] },
      { label: "Costo mensual predecible", values: ["no", "yes", "no", "yes"] },
    ],
    faqTitle: "Preguntas que hacen las agencias ABA pequeñas antes de probar ABAcases",
    faqs: [
      { q: "¿Los proveedores de mi área verán mi caso?", a: "Sí. Cada proveedor compatible cerca de tu caso recibe una alerta, y puedes ver quién está cerca en el mapa de proveedores. Fuera de Florida hoy hay menos proveedores, así que revisa primero el mapa cerca de tu caso." },
      {
        q: "¿Hay una forma gratuita de publicar casos ABA?",
        a: p.isPromoActive
          ? `Sí. Durante el periodo gratis, ${count(p.count, "tu primera vacante es gratis", `tus primeras ${p.count} vacantes son gratis`)} hasta el ${p.endDate}. Desde el ${p.startDate}, Starter cuesta $${p.starterMo} al mes e incluye ${p.starterIncluded} ${count(p.starterIncluded, "vacante", "vacantes")}.`
          : `El periodo gratis terminó. Starter cuesta $${p.starterMo} al mes e incluye ${p.starterIncluded} ${count(p.starterIncluded, "vacante", "vacantes")}. Al cerrar o cubrir una vacante, deja de contar para tu plan.`,
      },
      {
        q: "¿Cuánto cuesta contratar a un RBT?",
        a: p.isPromoActive
          ? `En ABAcases, Starter cuesta $${p.starterMo} al mes desde el ${p.startDate}, con ${p.starterIncluded} ${count(p.starterIncluded, "vacante incluida", "vacantes incluidas")}. Las bolsas de pago por clic cobran por cada visitante y los reclutadores suelen cobrar una comisión por cada contratación.`
          : `En ABAcases, Starter cuesta $${p.starterMo} al mes, con ${p.starterIncluded} ${count(p.starterIncluded, "vacante incluida", "vacantes incluidas")}. Las bolsas de pago por clic cobran por cada visitante y los reclutadores suelen cobrar una comisión por cada contratación.`,
      },
      {
        q: "¿En qué se diferencia de publicar en un grupo de Facebook?",
        a: "Los grupos sociales ofrecen alcance, pero las respuestas quedan dispersas entre comentarios y mensajes. ABAcases mantiene casos, candidatos, credenciales, comparación, conversaciones y etapas en un solo flujo.",
      },
      {
        q: "¿Necesito una tarjeta de crédito para comenzar?",
        a: p.isPromoActive
          ? `No. Durante el periodo gratis puedes abrir una cuenta de agencia y publicar tus vacantes sin añadir una tarjeta.`
          : `Puedes abrir una cuenta de agencia sin tarjeta de crédito. Starter cuesta $${p.starterMo} al mes e incluye ${p.starterIncluded} ${count(p.starterIncluded, "vacante", "vacantes")}.`,
      },
      {
        q: "¿Qué pasa si solo contrato una o dos veces al año?",
        a: "Las vacantes son reutilizables. Publica un caso, cúbrelo o ciérralo y usa el mismo lugar de tu plan cuando surja otra necesidad.",
      },
      {
        q: "¿Puedo ver proveedores antes de que soliciten?",
        a: "Sí. El mapa de proveedores está abierto en todos los planes. Muestra información relevante y la distancia alrededor de un caso, y puedes filtrar proveedores por idioma, incluido el español. También puedes invitar a proveedores compatibles a postularse.",
      },
    ],
    localQ: "¿Contratas en Florida?",
    localFl: "Guía de Florida",
    localMiami: "Página de Miami",
  };
};

/** A single "What one case costs" card. `highlight` marks the ABAcases card. */
function CostCard({ name, sub, big, lead, body, small, highlight }) {
  return (
    <div
      data-testid={highlight ? "cost-card-abacases" : "cost-card"}
      style={{
        background: "#fff",
        border: highlight ? `2px solid ${INDIGO}` : `1px solid ${BORDER}`,
        borderRadius: 20,
        padding: "26px 24px",
        boxShadow: highlight ? "0 18px 44px rgba(79,70,229,0.20)" : "0 4px 18px rgba(15,23,42,0.05)",
        display: "flex", flexDirection: "column", boxSizing: "border-box", minWidth: 0,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: highlight ? INDIGO : "#64748B" }}>{name}</div>
      <div style={{ fontSize: 13, color: T.textSubtle, fontWeight: 600, marginBottom: 14 }}>{sub}</div>
      <div style={{ fontSize: highlight ? 58 : 34, lineHeight: 1.05, fontWeight: 900, letterSpacing: "-0.03em", color: highlight ? INDIGO : "#64748B", margin: highlight ? "0 0 4px" : "10px 0 14px" }}>{big}</div>
      {lead && <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 12 }}>{lead}</div>}
      <p style={{ fontSize: 15, lineHeight: 1.6, color: TEXT, margin: 0 }}>{body}</p>
      {small && <p style={{ fontSize: 13, color: T.textSubtle, margin: "10px 0 0" }}>{small}</p>}
    </div>
  );
}

export default function AbaStaffingForSmallAgencies({ onNavigate }) {
  useConfigVersion(); // re-render with the Admin prices once platform_config loads
  const { t, i18n } = useTranslation();
  const isEs = String(i18n.resolvedLanguage || i18n.language || "").toLowerCase().startsWith("es");
  const promoDates = getStarterPromoDates();
  const pricing = getAgencyPricing(isEs ? "es" : "en");
  const p = { ...pricing, endDateShort: isEs ? promoDates.endDateShortStrEs : promoDates.endDateShortStr, note: outsideFloridaText(t, pricing.isPromoActive) };
  const c = (isEs ? ES : EN)(p);
  const en = EN({ ...pricing, endDateShort: promoDates.endDateShortStr, note: outsideFloridaText(i18n.getFixedT("en"), pricing.isPromoActive) }); // canonical English (SEO + JSON-LD)

  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 760);
  const [compareIdx, setCompareIdx] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  usePageSeo({
    title: "Affordable ABA Staffing for Small Agencies | Find RBTs & BCBAs",
    description: pricing.isPromoActive
      ? `ABA staffing without recruiter fees or job-board spend. Your first ${pricing.promoCount} ABA openings are free through ${promoDates.endDateShortStr}.`
      : "Compare practical ways to find RBTs and BCBAs without recruiter fees or unpredictable job-board spend.",
    canonical: "/aba-staffing-for-small-agencies",
    ogTitle: "How Small ABA Agencies Find Providers Without a Big Ad Budget",
    ogDescription: "A practical cost comparison for small ABA practices hiring RBTs, BCBAs, BCaBAs, and BTs.",
  });
  useSiteSchema();
  useFaqJsonLd("faq-jsonld-aba-staffing-small-agencies", en.faqs);
  useBreadcrumbJsonLd("bc-jsonld-aba-staffing-small-agencies", [
    { name: "Home", url: "/" },
    { name: "For ABA Agencies", url: "/aba-agencies" },
    { name: "ABA Staffing for Small Agencies", url: "/aba-staffing-for-small-agencies" },
  ]);

  const sectionPad = isMobile ? "46px 18px" : "68px 48px";
  const maxW = 980;
  const h2 = { color: NAVY, fontSize: isMobile ? 25 : 32, margin: "0 0 24px", letterSpacing: "-.025em", textAlign: "center" };
  const alt = { background: ALT_BG, borderBlock: `1px solid ${BORDER}` };

  const Link = ({ to, children, style }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to === "/" ? "home" : to.replace(/^\//, "")); }} style={{ color: INDIGO, fontWeight: 700, ...style }}>{children}</a>
  );
  const { label: ctaLabel, go: goSignup } = useAgencySignup({ source: "aba_staffing_for_small_agencies", pagePath: "/aba-staffing-for-small-agencies/", onNavigate });
  const signup = () => goSignup("hero");

  // Scorecard: full 5-column table on desktop; on mobile one competitor vs ABAcases.
  const abaIdx = c.channels.length - 1;
  const cmpIdx = Math.min(compareIdx, abaIdx - 1);
  const scoreColumns = isMobile ? [c.channels[cmpIdx], c.channels[abaIdx]] : c.channels;
  const scoreRows = c.rows.map((r) => ({
    label: r.label,
    values: isMobile ? [r.values[cmpIdx], r.values[abaIdx]] : r.values,
  }));

  return (
    <div lang={isEs ? "es" : "en"} style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>
      <header style={{ background: `linear-gradient(135deg, ${NAVY}, ${NAVY_MID})`, padding: isMobile ? "28px 18px 50px" : "34px 48px 72px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", width: 460, height: 460, borderRadius: "50%", background: "rgba(79,70,229,.22)", filter: "blur(90px)", right: "4%", top: -180 }} />
        <div style={{ maxWidth: maxW, margin: "0 auto", position: "relative" }}>
          <nav aria-label="Breadcrumb" style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap", marginBottom: 22, fontSize: 12.5 }}>
            <Link to="/" style={{ color: INDIGO_LT, textDecoration: "none" }}>{c.home}</Link><ChevronRightIcon size={12} color="#94A3B8" />
            <Link to="/aba-agencies" style={{ color: INDIGO_LT, textDecoration: "none" }}>{c.forAgencies}</Link><ChevronRightIcon size={12} color="#94A3B8" />
            <span style={{ color: "#CBD5E1" }}>{c.crumb}</span>
          </nav>
          <div style={{ display: "inline-block", color: "#C7D2FE", background: "rgba(255,255,255,.09)", padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 800, marginBottom: 14 }}>{c.eyebrow}</div>
          <h1 style={{ maxWidth: 820, margin: "0 0 18px", color: "#fff", fontSize: isMobile ? 32 : 50, lineHeight: 1.1, letterSpacing: "-.035em", fontWeight: 900 }}>{c.h1}</h1>
          <p style={{ maxWidth: 720, margin: "0 0 28px", color: "#CBD5E1", fontSize: isMobile ? 16 : 18, lineHeight: 1.65 }}>{c.sub}</p>
          <button onClick={signup} style={{ border: 0, borderRadius: 11, background: T.gradientIndigo, color: "#fff", padding: "14px 25px", fontWeight: 800, fontSize: 15, cursor: "pointer" }}>{ctaLabel}</button>
          <div style={{ color: "#A5B4FC", fontSize: 14, marginTop: 11 }}>{c.heroPrice}</div>
          <div style={{ marginTop: 20 }}>
            <ProofChips dark items={agencyChips(t)} />
          </div>
        </div>
      </header>

      {/* 1. Will anyone see it: distribution + honest note for newer areas */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2}>{t("agencyVisuals.promoTitle")}</h2>
          <CasePromotion isMobile={isMobile} />
          <OutsideFloridaNote isMobile={isMobile} />
        </div>
      </section>

      {/* 2. What one case costs: the visual centerpiece */}
      <section style={{ padding: sectionPad, ...alt }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ ...h2, margin: "0 0 8px" }}>{c.costTitle}</h2>
          <p style={{ textAlign: "center", color: TEXT, fontSize: 15.5, margin: "0 0 30px" }}>{c.costSub}</p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(3, minmax(0, 1fr))", gap: isMobile ? 16 : 22, alignItems: "stretch" }}>
            <CostCard {...c.cards.click} />
            <CostCard {...c.cards.recruiter} />
            <CostCard {...c.cards.abacases} highlight />
          </div>
          <p data-testid="pro-line" style={{ textAlign: "center", color: TEXT, fontSize: 14.5, margin: "22px 0 0" }}>{c.proLine}</p>
        </div>
      </section>

      {/* 4. How the options compare */}
      <section style={{ padding: sectionPad, ...alt }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2}>{c.compareTitle}</h2>
          {isMobile && (
            <div role="group" aria-label={c.compareWith} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>{c.compareWith}</span>
              <div style={{ display: "inline-flex", background: "#E2E8F0", borderRadius: 11, padding: 3 }}>
                {c.channels.slice(0, abaIdx).map((ch, i) => (
                  <button key={ch.title} type="button" aria-pressed={cmpIdx === i} onClick={() => setCompareIdx(i)} style={{ border: 0, borderRadius: 8, padding: "8px 11px", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", background: cmpIdx === i ? "#fff" : "transparent", color: cmpIdx === i ? INDIGO : TEXT, boxShadow: cmpIdx === i ? "0 1px 4px rgba(15,23,42,0.12)" : "none" }}>{ch.short}</button>
                ))}
              </div>
            </div>
          )}
          <Scorecard isMobile={false} columns={scoreColumns} rows={scoreRows} />
        </div>
      </section>

      {/* 5. Your ABA recruiting system (agencyVisuals.diffTitle) */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2}>{t("agencyVisuals.diffTitle")}</h2>
          <DifferentialGrid sample isMobile={isMobile} />
        </div>
      </section>

      {/* 6. FAQ + local guides */}
      <section style={{ padding: sectionPad, ...alt }}>
        <div style={{ maxWidth: 780, margin: "0 auto" }}>
          <h2 style={{ ...h2, margin: "0 0 25px" }}>{c.faqTitle}</h2>
          {/* Same look as the /aba-agencies FAQ ("+" rows). <details> keeps every answer in the HTML for SEO. */}
          {c.faqs.map((f, i) => <FaqDetails key={f.q} q={f.q} a={f.a} defaultOpen={i === 0} isMobile={isMobile} />)}
          <p style={{ color: TEXT, textAlign: "center", marginTop: 26, fontSize: 13.5 }}>
            {c.localQ} <Link to="/aba-staffing-florida">{c.localFl}</Link> · <Link to="/rbt-staffing-miami-fl">{c.localMiami}</Link>
          </p>
        </div>
      </section>

      {/* 6. Questions + who is behind ABAcases: the page ends here, no closing CTA band */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ ...h2, margin: "0 0 8px" }}>{c.founderLine}</h2>
          <div style={{ height: isMobile ? 14 : 20 }} />
          <FounderCard isMobile={isMobile} onMessage={() => setContactOpen(true)} />
        </div>
      </section>

      {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
      <PublicFooter />
      <StickyMobileCta label={ctaLabel} onClick={() => goSignup("sticky")} ctaId="agency" />
    </div>
  );
}
